import {
  AndroidIcon,
  AppleIcon,
  BatteryIcon,
  CompatibilityIcon,
  FeaturesLeftArrowIcon,
  FeaturesRightArrowIcon,
  NFCIcon,
  PawIcon,
  QRIcon,
  WaterProofIcon,
} from "@/components/data-display/Icon";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import FeatureCard from "./FeatureCard";
import Image from "next/image";
import SectionHeading from "../SectionHeading";
import { useRef } from "react";

const responsive = {
  desktopSmall: {
    breakpoint: { max: 4000, min: 1280 },
    items: 4,
    slidesToSlide: 4,
  },
  laptop: {
    breakpoint: { max: 1279, min: 1024 },
    items: 3,
    slidesToSlide: 3,
  },
  tablet: {
    breakpoint: { max: 1023, min: 768 },
    items: 2,
    slidesToSlide: 2,
  },
  mobile: {
    breakpoint: { max: 767, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
};

const featuresData = [
  {
    Icon: QRIcon,
    title: "Universal QR Code Compatibility",
    description:
      "Easily scannable QR code works with any device, ensuring your pet’s information is always accessible.",
  },
  {
    Icon: NFCIcon,
    title: "NFC Chip for Instant Sharing",
    description:
      "Tap to instantly share your contact details, making it effortless for anyone to help reunite you with your pet.",
  },
  {
    Icon: WaterProofIcon,
    title: "Waterproof & Durable Design",
    description:
      "Built to endure all weather conditions and your pet’s adventurous lifestyle.",
  },
  {
    Icon: BatteryIcon,
    title: "No Batteries Needed",
    description:
      "Always ready to go—no charging or batteries required, so you never have to worry about downtime.",
  },
  {
    Icon: CompatibilityIcon,
    title: "Global Compatibility",
    description:
      "Works seamlessly with both iPhone and Android devices, making it easy for anyone to access your pet’s information.",
  },
  {
    Icon: AndroidIcon,
    title: "Lightweight & Pet-Friendly",
    description:
      "Designed to be lightweight and comfortable, so your pet won’t even notice it’s there.",
  },
  {
    Icon: PawIcon,
    title: "Customizable Pet Profile",
    description:
      "Create a personalized profile for your pet that can include medical information, dietary needs, and more.",
  },
  {
    Icon: AppleIcon,
    title: "Affordable & Easy to Use",
    description:
      "High-quality protection for your pet at an affordable price, with an easy setup process anyone can follow.",
  },
];

function PetTagFeaturesCarousel() {
  const leftArrowRef = useRef(null);
  const rightArrowRef = useRef(null);

  const CustomLeftArrow = ({ onClick }) => {
    return (
      <button
        onClick={() => onClick()}
        className="absolute left-0 top-[45%] opacity-0"
        ref={leftArrowRef}
      >
        <FeaturesLeftArrowIcon />
      </button>
    );
  };

  const CustomRightArrow = ({ onClick }) => {
    return (
      <button
        onClick={() => onClick()}
        className="absolute -right-[6rem] top-[45%] opacity-0"
        ref={rightArrowRef}
      >
        <FeaturesRightArrowIcon />
      </button>
    );
  };

  return (
    <div className="overflow-hidden py-[5rem]">
      <SectionHeading title="Our Main Features" className="mb-[3.125rem]" />
      <div className="relative">
        <div className="absolute w-[80%] h-[140%] lg:h-full -top-[5rem] md:-top-[4rem] lg:top-[5rem] -left-[5rem]">
          <Image
            src="/assets/images/feature-bg-violet.png"
            fill
            alt=""
            quality={30}
          />
        </div>
        <div className="absolute w-[70%] -top-[5rem] lg:-top-[5rem] h-[130%] lg:h-[140%] -right-[3rem] 2xl:right-[2rem]">
          <Image
            src="/assets/images/feature-bg-red.png"
            fill
            alt=""
            quality={30}
          />
        </div>

        <div className="relative max-w-[18.5rem] mx-auto md:max-w-[37rem] lg:max-w-[55.5rem] xl:max-w-[74rem]">
          <button
            onClick={() => leftArrowRef.current.click()}
            className="absolute z-10 top-[50%] -translate-y-[50%] -left-[2.1rem] min-[400px]:-left-[3rem] lg:-left-[4rem]"
          >
            <FeaturesLeftArrowIcon className="h-[2.25rem] w-[2.25rem] lg:h-[2.75rem] lg:w-[2.75rem] shadow-arrow-icon rounded-full" />
          </button>

          <button
            onClick={() => rightArrowRef.current.click()}
            className="absolute z-10 top-[50%] -translate-y-[50%] -right-[2.1rem] min-[400px]:-right-[3rem] lg:-right-[4rem]"
          >
            <FeaturesRightArrowIcon className="h-[2.25rem] w-[2.25rem] lg:h-[2.75rem] lg:w-[2.75rem] shadow-arrow-icon rounded-full" />
          </button>

          <Carousel
            additionalTransfrom={0}
            centerMode={false}
            containerClass="max-w-[18.5rem] mx-auto md:max-w-[37rem] lg:max-w-[55.5rem] xl:max-w-[74rem]"
            customLeftArrow={<CustomLeftArrow />}
            customRightArrow={<CustomRightArrow />}
            draggable={false}
            focusOnSelect={false}
            infinite={true}
            itemClass="flex justify-center px-[0.25rem]"
            keyBoardControl
            minimumTouchDrag={80}
            renderArrowsWhenDisabled={false}
            renderButtonGroupOutside={false}
            renderDotsOutside={false}
            responsive={responsive}
            rewind={false}
            rewindWithAnimation={false}
            rtl={false}
            showDots={false}
            swipeable={false}
            transitionDuration={500}
          >
            {featuresData.map((feature, index) => {
              return <FeatureCard key={index} {...feature} />;
            })}
          </Carousel>
        </div>
      </div>
    </div>
  );
}

export default PetTagFeaturesCarousel;
